import { sign } from '@stablelib/ed25519';
import axios from 'axios';
import { getHexHash, toHex, recipientString, openCryptobox, encryptCryptoboxPayload, decryptCryptoboxPayload, secretbox_NONCEBYTES, secretbox_MACBYTES, getKeypairFromSeed } from '@airgap/beacon-utils';
import { MatrixClient } from '../matrix-client/MatrixClient';
import { MatrixClientEventType } from '../matrix-client/models/MatrixClientEvent';
import { MatrixMessageType } from '../matrix-client/models/MatrixMessage';
import { P2PPairingRequest, StorageKey, P2PPairingResponse, Regions } from '@airgap/beacon-types';
import { PeerManager, BEACON_VERSION, getSenderId, Logger, CommunicationClient } from '@airgap/beacon-core';
import { ExposedPromise, generateGUID } from '@airgap/beacon-utils';
import { hash } from '@stablelib/blake2b';
import { encode } from '@stablelib/utf8';
const logger = new Logger('P2PCommunicationClient');
const RESPONSE_WAIT_TIME_MS = 60_000; // total wait time for all the probes
const REGIONS_AND_SERVERS = {
    [Regions.EUROPE_WEST]: [
        'beacon-node-1.diamond.papers.tech',
        'beacon-node-1.sky.papers.tech',
        'beacon-node-2.sky.papers.tech',
        'beacon-node-1.hope.papers.tech',
        'beacon-node-1.hope-2.papers.tech',
        'beacon-node-1.hope-3.papers.tech',
        'beacon-node-1.hope-4.papers.tech',
        'beacon-node-1.hope-5.papers.tech'
    ],
    [Regions.NORTH_AMERICA_EAST]: ['beacon-node-1.beacon-server-1.papers.tech'],
    [Regions.NORTH_AMERICA_WEST]: ['beacon-node-1.beacon-server-2.papers.tech'],
    [Regions.ASIA_EAST]: ['beacon-node-1.beacon-server-3.papers.tech'],
    [Regions.AUSTRALIA]: ['beacon-node-1.beacon-server-4.papers.tech']
};
const sleep = (time) => {
    return new Promise((resolve) => setTimeout(resolve, time));
};
/**
 * @internalapi
 */
export class P2PCommunicationClient extends CommunicationClient {
    name;
    replicationCount;
    storage;
    iconUrl;
    appUrl;
    client = new ExposedPromise();
    initialEvent;
    initialListener;
    selectedRegion;
    ENABLED_RELAY_SERVERS;
    relayServer;
    activeListeners = new Map();
    ignoredRooms = [];
    loginCounter = 0;
    constructor(name, keyPair, replicationCount, storage, matrixNodes, iconUrl, appUrl) {
        super(keyPair);
        this.name = name;
        this.replicationCount = replicationCount;
        this.storage = storage;
        this.iconUrl = iconUrl;
        this.appUrl = appUrl;
        logger.log('constructor', 'P2PCommunicationClient created');
        this.ENABLED_RELAY_SERVERS = REGIONS_AND_SERVERS;
        if (matrixNodes) {
            this.ENABLED_RELAY_SERVERS = {
                ...REGIONS_AND_SERVERS,
                ...matrixNodes
            };
        }
    }
    async getPairingRequestInfo() {
        const info = new P2PPairingRequest(await generateGUID(), this.name, await this.getPublicKey(), BEACON_VERSION, (await this.getRelayServer()).server);
        if (this.iconUrl) {
            info.icon = this.iconUrl;
        }
        if (this.appUrl) {
            info.appUrl = this.appUrl;
        }
        return info;
    }
    async getPairingResponseInfo(request) {
        const info = new P2PPairingResponse(request.id, this.name, await this.getPublicKey(), request.version, (await this.getRelayServer()).server);
        if (this.iconUrl) {
            info.icon = this.iconUrl;
        }
        if (this.appUrl) {
            info.appUrl = this.appUrl;
        }
        return info;
    }
    /**
     * To get the fastest region, we can't simply do one request, because sometimes,
     * DNS and SSL handshakes make "faster" connections slower. So we need to do 2 requests
     * and check which request was the fastest after 1s.
     */
    async findBestRegionAndGetServer() {
        if (this.selectedRegion) {
            return this.relayServer?.promiseResult;
        }
        const probes = Object.entries(this.ENABLED_RELAY_SERVERS)
            .flatMap(([region, servers]) => servers.map((server) => ({ server, region: region })))
            .sort(() => Math.random() - 0.5);
        const results = [];
        const probePromises = probes.map(({ server, region }) => (async () => {
            const start = Date.now();
            try {
                const info = await this.getBeaconInfo(server);
                results.push({
                    server,
                    region,
                    time: Date.now() - start,
                    timestamp: info.timestamp
                });
            }
            catch (err) {
                logger.warn(`probe for ${server} failed:`, err);
                // swallow the error so Promise.all never rejects
            }
        })());
        // 3) Wait until either:
        //    • all probes settle (fast regions), or
        //    • we hit our global timeout
        await Promise.race([Promise.all(probePromises), sleep(RESPONSE_WAIT_TIME_MS)]);
        // 4) If nobody replied, bail out
        if (results.length === 0) {
            throw new Error('No server responded.');
        }
        // 5) Pick the lowest-latency reply
        const best = results.reduce((a, b) => (b.time < a.time ? b : a));
        this.selectedRegion = best.region;
        return { server: best.server, timestamp: best.timestamp };
    }
    async getRelayServer() {
        if (this.relayServer) {
            const relayServer = await this.relayServer.promise;
            // We make sure the locally cached timestamp is not older than 1 minute, if it is, we refresh it
            if (Date.now() - relayServer.localTimestamp < 60 * 1000) {
                return { server: relayServer.server, timestamp: relayServer.timestamp };
            }
            const info = await this.getBeaconInfo(relayServer.server);
            this.relayServer.resolve({
                server: relayServer.server,
                timestamp: info.timestamp,
                localTimestamp: new Date().getTime()
            });
            return { server: relayServer.server, timestamp: info.timestamp };
        }
        else {
            this.relayServer = new ExposedPromise();
        }
        const node = await this.storage.get(StorageKey.MATRIX_SELECTED_NODE);
        if (node && node.length > 0) {
            const info = await this.getBeaconInfo(node);
            this.relayServer.resolve({
                server: node,
                timestamp: info.timestamp,
                localTimestamp: new Date().getTime()
            });
            return { server: node, timestamp: info.timestamp };
        }
        const server = await this.findBestRegionAndGetServer();
        if (!server) {
            throw new Error(`No servers found`);
        }
        this.storage
            .set(StorageKey.MATRIX_SELECTED_NODE, server.server)
            .catch((error) => logger.log(error));
        this.relayServer.resolve({
            server: server.server,
            timestamp: server.timestamp,
            localTimestamp: new Date().getTime()
        });
        return { server: server.server, timestamp: server.timestamp };
    }
    async getBeaconInfo(server) {
        return axios
            .get(`https://${server}/_synapse/client/beacon/info`)
            .then((res) => ({
            region: res.data.region,
            known_servers: res.data.known_servers,
            timestamp: Math.floor(res.data.timestamp)
        }));
    }
    async tryJoinRooms(roomId, retry = 1) {
        try {
            await (await this.client.promise).joinRooms(roomId);
        }
        catch (error) {
            if (retry <= 10 && error.errcode === 'M_FORBIDDEN') {
                // If we join the room too fast after receiving the invite, the server can accidentally reject our join. This seems to be a problem only when using a federated multi-node setup. Usually waiting for a couple milliseconds solves the issue, but to handle lag, we will keep retrying for 2 seconds.
                logger.log(`Retrying to join...`, error);
                setTimeout(async () => {
                    await this.tryJoinRooms(roomId, retry + 1);
                }, 200);
            }
            else {
                logger.log(`Failed to join after ${retry} tries.`, error);
            }
        }
    }
    async start() {
        logger.log('start', 'starting client');
        logger.log('start', `connecting to server`);
        const relayServer = await this.getRelayServer();
        const client = MatrixClient.create({
            baseUrl: `https://${relayServer.server}`,
            storage: this.storage
        });
        this.initialListener = async (event) => {
            if (this.initialEvent && this.initialEvent.timestamp && event && event.timestamp) {
                if (this.initialEvent.timestamp < event.timestamp) {
                    this.initialEvent = event;
                }
            }
            else {
                this.initialEvent = event;
            }
        };
        client.subscribe(MatrixClientEventType.MESSAGE, this.initialListener);
        client.subscribe(MatrixClientEventType.INVITE, async (event) => {
            let member;
            if (event.content.members.length === 1) {
                // If there is only one member we know it's a new room
                // TODO: Use the "sender" of the event instead
                member = event.content.members[0];
            }
            await this.tryJoinRooms(event.content.roomId);
            if (member) {
                await this.updateRelayServer(member);
                await this.updatePeerRoom(member, event.content.roomId);
            }
        });
        if (!relayServer.timestamp) {
            throw new Error('No timestamp received from relay server');
        }
        const time = Math.floor(relayServer.timestamp);
        const loginString = `login:${Math.floor(time / (5 * 60))}`;
        logger.log('start', `login ${loginString}, ${await this.getPublicKeyHash()} on ${relayServer.server}`);
        const loginRawDigest = hash(encode(loginString), 32);
        const secretKey = this.keyPair.secretKey ?? this.keyPair.privateKey;
        const rawSignature = sign(secretKey, loginRawDigest);
        try {
            await client.start({
                id: await this.getPublicKeyHash(),
                password: `ed:${toHex(rawSignature)}:${await this.getPublicKey()}`,
                deviceId: toHex(this.keyPair.publicKey)
            });
        }
        catch (error) {
            logger.error('start', 'Could not log in, retrying', error);
            if (error.errcode === 'M_USER_DEACTIVATED') {
                await this.generateNewKeyPair();
                await this.reset();
                throw new Error('The account is deactivated.');
            }
            await this.reset(); // If we can't log in, let's reset
            if (!this.selectedRegion) {
                throw new Error('No region selected.');
            }
            if (this.loginCounter <= (this.ENABLED_RELAY_SERVERS[this.selectedRegion] ?? []).length) {
                this.loginCounter++;
                this.start();
                return;
            }
            else {
                logger.error('start', 'Tried to log in to every known beacon node, but no login was successful.');
                throw new Error('Could not connect to any beacon nodes. Try again later.');
            }
        }
        logger.log('start', 'login successful, client is ready');
        this.client.resolve(client);
    }
    async stop() {
        logger.log('stop', 'stopping client');
        if (this.client.isResolved()) {
            await (await this.client.promise).stop().catch((error) => logger.error(error));
        }
        await this.reset();
    }
    async reset() {
        logger.log('reset', 'resetting connection');
        await this.storage.delete(StorageKey.MATRIX_PEER_ROOM_IDS).catch((error) => logger.log(error));
        await this.storage.delete(StorageKey.MATRIX_PRESERVED_STATE).catch((error) => logger.log(error));
        await this.storage.delete(StorageKey.MATRIX_SELECTED_NODE).catch((error) => logger.log(error));
        // Instead of resetting everything, maybe we should make sure a new instance is created?
        this.relayServer = undefined;
        this.client = new ExposedPromise();
        this.initialEvent = undefined;
        this.initialListener = undefined;
    }
    async listenForEncryptedMessage(senderPublicKey, messageCallback) {
        if (this.activeListeners.has(senderPublicKey)) {
            return;
        }
        logger.log('listenForEncryptedMessage', `start listening for encrypted messages from publicKey ${senderPublicKey}`);
        const sharedKey = await this.createCryptoBoxServer(senderPublicKey, this.keyPair);
        const callbackFunction = async (event) => {
            if (this.isTextMessage(event.content) && (await this.isSender(event, senderPublicKey))) {
                let payload;
                await this.updateRelayServer(event.content.message.sender);
                await this.updatePeerRoom(event.content.message.sender, event.content.roomId);
                try {
                    payload = Buffer.from(event.content.message.content, 'hex');
                    // content can be non-hex if it's a connection open request
                }
                catch {
                    /* */
                }
                if (payload && payload.length >= secretbox_NONCEBYTES + secretbox_MACBYTES) {
                    try {
                        const decryptedMessage = await decryptCryptoboxPayload(payload, sharedKey.receive);
                        logger.log('listenForEncryptedMessage', `received a message from ${senderPublicKey}`, decryptedMessage);
                        // logger.log(
                        //   'listenForEncryptedMessage',
                        //   'encrypted message received',
                        //   decryptedMessage,
                        //   await new Serializer().deserialize(decryptedMessage)
                        // )
                        // console.log('calculated sender ID', await getSenderId(senderPublicKey))
                        // TODO: Add check for correct decryption key / sender ID
                        messageCallback(decryptedMessage);
                    }
                    catch (decryptionError) {
                        /* NO-OP. We try to decode every message, but some might not be addressed to us. */
                    }
                }
            }
        };
        this.activeListeners.set(senderPublicKey, callbackFunction);
        (await this.client.promise).subscribe(MatrixClientEventType.MESSAGE, callbackFunction);
        const lastEvent = this.initialEvent;
        if (lastEvent &&
            lastEvent.timestamp &&
            new Date().getTime() - lastEvent.timestamp < 5 * 60 * 1000) {
            logger.log('listenForEncryptedMessage', 'Handling previous event');
            await callbackFunction(lastEvent);
        }
        else {
            logger.log('listenForEncryptedMessage', 'No previous event found');
        }
        const initialListener = this.initialListener;
        if (initialListener) {
            ;
            (await this.client.promise).unsubscribe(MatrixClientEventType.MESSAGE, initialListener);
        }
        this.initialListener = undefined;
        this.initialEvent = undefined;
    }
    async unsubscribeFromEncryptedMessage(senderPublicKey) {
        const listener = this.activeListeners.get(senderPublicKey);
        if (!listener) {
            return;
        }
        ;
        (await this.client.promise).unsubscribe(MatrixClientEventType.MESSAGE, listener);
        this.activeListeners.delete(senderPublicKey);
    }
    async unsubscribeFromEncryptedMessages() {
        ;
        (await this.client.promise).unsubscribeAll(MatrixClientEventType.MESSAGE);
        this.activeListeners.clear();
    }
    async sendMessage(message, peer) {
        const sharedKey = await this.createCryptoBoxClient(peer.publicKey, this.keyPair);
        const recipientHash = await getHexHash(Buffer.from(peer.publicKey, 'hex'));
        const recipient = recipientString(recipientHash, peer.relayServer);
        const roomId = await this.getRelevantRoom(recipient);
        // Before we send the message, we have to wait for the join to be accepted.
        // await this.waitForJoin(roomId) // TODO: This can probably be removed because we are now waiting inside the get room method
        const encryptedMessage = await encryptCryptoboxPayload(message, sharedKey.send);
        logger.log('sendMessage', 'sending encrypted message', peer.publicKey, roomId, message);
        (await this.client.promise).sendTextMessage(roomId, encryptedMessage).catch(async (error) => {
            if (error.errcode === 'M_FORBIDDEN') {
                // Room doesn't exist
                logger.log(`sendMessage`, `M_FORBIDDEN`, roomId, error);
                await this.deleteRoomIdFromRooms(roomId);
                const newRoomId = await this.getRelevantRoom(recipient);
                logger.log(`sendMessage`, `Old room deleted, new room created`, newRoomId);
                (await this.client.promise)
                    .sendTextMessage(newRoomId, encryptedMessage)
                    .catch(async (error2) => {
                    logger.log(`sendMessage`, `inner error`, newRoomId, error2);
                });
            }
            else {
                logger.log(`sendMessage`, `unexpected error`, error);
            }
        });
    }
    async updatePeerRoom(sender, roomId) {
        logger.log(`updatePeerRoom`, sender, roomId);
        // Sender is in the format "@pubkeyhash:relayserver.tld"
        const split = sender.split(':');
        if (split.length < 2 || !split[0].startsWith('@')) {
            throw new Error('Invalid sender');
        }
        const roomIds = await this.storage.get(StorageKey.MATRIX_PEER_ROOM_IDS);
        const room = roomIds[sender];
        if (room === roomId) {
            logger.debug(`updatePeerRoom`, `rooms are the same, not updating`);
        }
        logger.debug(`updatePeerRoom`, `current room`, room, 'new room', roomId);
        if (room && room[1]) {
            // If we have a room already, let's ignore it. We need to do this, otherwise it will be loaded from the matrix cache.
            logger.log(`updatePeerRoom`, `adding room "${room[1]}" to ignored array`);
            this.ignoredRooms.push(room[1]);
        }
        roomIds[sender] = roomId;
        await this.storage.set(StorageKey.MATRIX_PEER_ROOM_IDS, roomIds);
        // TODO: We also need to delete the room from the sync state
        // If we need to delete a room, we can assume the local state is not up to date anymore, so we can reset the state
    }
    async deleteRoomIdFromRooms(roomId) {
        const roomIds = await this.storage.get(StorageKey.MATRIX_PEER_ROOM_IDS);
        const newRoomIds = Object.entries(roomIds)
            .filter((entry) => entry[1] !== roomId)
            .reduce((pv, cv) => ({ ...pv, [cv[0]]: cv[1] }), {});
        await this.storage.set(StorageKey.MATRIX_PEER_ROOM_IDS, newRoomIds);
        // TODO: We also need to delete the room from the sync state
        // If we need to delete a room, we can assume the local state is not up to date anymore, so we can reset the state
        this.ignoredRooms.push(roomId);
    }
    async listenForChannelOpening(messageCallback) {
        logger.debug(`listenForChannelOpening`);
        (await this.client.promise).subscribe(MatrixClientEventType.MESSAGE, async (event) => {
            if (this.isTextMessage(event.content) && (await this.isChannelOpenMessage(event.content))) {
                logger.log(`listenForChannelOpening`, `channel opening received, trying to decrypt`, JSON.stringify(event));
                await this.updateRelayServer(event.content.message.sender);
                await this.updatePeerRoom(event.content.message.sender, event.content.roomId);
                const splits = event.content.message.content.split(':');
                const payload = Buffer.from(splits[splits.length - 1], 'hex');
                if (payload.length >= secretbox_NONCEBYTES + secretbox_MACBYTES) {
                    try {
                        const pairingResponse = JSON.parse(await openCryptobox(payload, this.keyPair.publicKey, this.keyPair.secretKey));
                        logger.log(`listenForChannelOpening`, `channel opening received and decrypted`, JSON.stringify(pairingResponse));
                        messageCallback({
                            ...pairingResponse,
                            senderId: await getSenderId(pairingResponse.publicKey)
                        });
                    }
                    catch (decryptionError) {
                        /* NO-OP. We try to decode every message, but some might not be addressed to us. */
                    }
                }
            }
        });
    }
    async waitForJoin(roomId, retry = 0) {
        // Rooms are updated as new events come in. `client.getRoomById` only accesses memory, it does not do any network requests.
        // TODO: Improve to listen to "JOIN" event
        const room = await (await this.client.promise).getRoomById(roomId);
        logger.log(`waitForJoin`, `Currently ${room.members.length} members, we need at least 2`);
        if (room.members.length >= 2) {
            return;
        }
        else {
            if (retry <= 200) {
                // On mobile, due to app switching, we potentially have to wait for a long time
                logger.log(`Waiting for join... Try: ${retry}`);
                return new Promise((resolve) => {
                    setTimeout(() => {
                        resolve(this.waitForJoin(roomId, retry + 1));
                    }, 100 * (retry > 50 ? 10 : 1)); // After the initial 5 seconds, retry only once per second
                });
            }
            else {
                throw new Error(`No one joined after ${retry} tries.`);
            }
        }
    }
    async sendPairingResponse(pairingRequest) {
        logger.log(`sendPairingResponse`);
        const recipientHash = await getHexHash(Buffer.from(pairingRequest.publicKey, 'hex'));
        const recipient = recipientString(recipientHash, pairingRequest.relayServer);
        // We force room creation here because if we "re-pair", we need to make sure that we don't send it to an old room.
        const roomId = await (await this.client.promise).createTrustedPrivateRoom(recipient);
        logger.debug(`sendPairingResponse`, `Connecting to room "${roomId}"`);
        await this.updatePeerRoom(recipient, roomId);
        // Before we send the message, we have to wait for the join to be accepted.
        await this.waitForJoin(roomId); // TODO: This can probably be removed because we are now waiting inside the get room method
        logger.debug(`sendPairingResponse`, `Successfully joined room.`);
        // TODO: remove v1 backwards-compatibility
        const message = typeof pairingRequest.version === 'undefined'
            ? await this.getPublicKey() // v1
            : JSON.stringify(await this.getPairingResponseInfo(pairingRequest)); // v2
        logger.debug(`sendPairingResponse`, `Sending pairing response`, message);
        const encryptedMessage = await this.encryptMessageAsymmetric(pairingRequest.publicKey, message);
        const msg = ['@channel-open', recipient, encryptedMessage].join(':');
        (await this.client.promise).sendTextMessage(roomId, msg).catch(async (error) => {
            if (error.errcode === 'M_FORBIDDEN') {
                // Room doesn't exist
                logger.log(`sendPairingResponse`, `M_FORBIDDEN`, roomId, error);
                await this.deleteRoomIdFromRooms(roomId);
                const newRoomId = await this.getRelevantRoom(recipient);
                logger.log(`sendPairingResponse`, `Old room deleted, new room created`, newRoomId);
                (await this.client.promise).sendTextMessage(newRoomId, msg).catch(async (error2) => {
                    logger.log(`sendPairingResponse`, `inner error`, newRoomId, error2);
                });
            }
            else {
                logger.log(`sendPairingResponse`, `unexpected error`, error);
            }
        });
    }
    isTextMessage(content) {
        return content.message.type === MatrixMessageType.TEXT;
    }
    async updateRelayServer(sender) {
        logger.log(`updateRelayServer`, sender);
        // Sender is in the format "@pubkeyhash:relayserver.tld"
        const split = sender.split(':');
        if (split.length < 2 || !split[0].startsWith('@')) {
            throw new Error('Invalid sender');
        }
        const senderHash = split.shift();
        const relayServer = split.join(':');
        const manager = localStorage.getItem('beacon:communication-peers-dapp')
            ? new PeerManager(this.storage, StorageKey.TRANSPORT_P2P_PEERS_DAPP)
            : new PeerManager(this.storage, StorageKey.TRANSPORT_P2P_PEERS_WALLET);
        const peers = await manager.getPeers();
        const promiseArray = peers.map(async (peer) => {
            const hash = `@${await getHexHash(Buffer.from(peer.publicKey, 'hex'))}`;
            if (hash === senderHash) {
                if (peer.relayServer !== relayServer) {
                    peer.relayServer = relayServer;
                    await manager.addPeer(peer);
                }
            }
        });
        await Promise.all(promiseArray);
    }
    async isChannelOpenMessage(content) {
        return content.message.content.startsWith(`@channel-open:@${await getHexHash(Buffer.from(await this.getPublicKey(), 'hex'))}`);
    }
    async isSender(event, senderPublicKey) {
        return event.content.message.sender.startsWith(`@${await getHexHash(Buffer.from(senderPublicKey, 'hex'))}`);
    }
    async generateNewKeyPair() {
        const newSeed = await generateGUID();
        console.warn(`The current user ID has been deactivated. Generating new ID: ${newSeed}`);
        this.storage.set(StorageKey.BEACON_SDK_SECRET_SEED, newSeed);
        this.keyPair = await getKeypairFromSeed(newSeed);
    }
    async getRelevantRoom(recipient) {
        const roomIds = await this.storage.get(StorageKey.MATRIX_PEER_ROOM_IDS);
        let roomId = roomIds[recipient];
        if (!roomId) {
            logger.log(`getRelevantRoom`, `No room found for peer ${recipient}, checking joined ones.`);
            const room = await this.getRelevantJoinedRoom(recipient);
            roomId = room.id;
            roomIds[recipient] = room.id;
            await this.storage.set(StorageKey.MATRIX_PEER_ROOM_IDS, roomIds);
        }
        logger.log(`getRelevantRoom`, `Using room ${roomId}`);
        return roomId;
    }
    async getRelevantJoinedRoom(recipient) {
        const joinedRooms = await (await this.client.promise).joinedRooms;
        logger.log('checking joined rooms', joinedRooms, recipient);
        const relevantRooms = joinedRooms
            .filter((roomElement) => !this.ignoredRooms.some((id) => roomElement.id === id))
            .filter((roomElement) => roomElement.members.some((member) => member === recipient));
        let room;
        // We always create a new room if one has been ignored. This is because if we ignore one, we know the server state changed.
        // So we cannot trust the current sync state. This can be removed once we have a method to properly clear and refresh the sync state.
        if (relevantRooms.length === 0 || this.ignoredRooms.length > 0) {
            logger.log(`getRelevantJoinedRoom`, `no relevant rooms found, creating new one`);
            const roomId = await (await this.client.promise).createTrustedPrivateRoom(recipient);
            room = await (await this.client.promise).getRoomById(roomId);
            logger.log(`getRelevantJoinedRoom`, `waiting for other party to join room: ${room.id}`);
            await this.waitForJoin(roomId);
            logger.log(`getRelevantJoinedRoom`, `new room created and peer invited: ${room.id}`);
        }
        else {
            room = relevantRooms[0];
            logger.log(`getRelevantJoinedRoom`, `channel already open, reusing room ${room.id}`);
        }
        return room;
    }
}
//# sourceMappingURL=P2PCommunicationClient.js.map